import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { DIVIDER_ID } from '@modernloop/shared/constants';
import { useFlag } from '@modernloop/shared/feature-flag';
import { SidebarOffIcon, SidebarOnIcon } from '@modernloop/shared/icons';
import { Box, Stack } from '@mui/material';
import { noop } from 'lodash';

import { ScheduleTask_TaskFragment, SourceType, TaskStatus } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import { useTaskTimelineVisibility } from 'src/contexts/TaskTimelineVisibilityProvider';

import useIsAiTaskEnabled from 'src/hooks/useIsAiTaskEnabled';

import ActivityFeed from 'src/views-new/ActivityFeed';
import BottomUpTaskTimelineFeed from 'src/views-new/ActivityFeed/BottomUpTaskTimelineFeed';

import useSetChromeExtensionWidth from 'src/shared/pages/extension/hooks/useSetChromeExtensionWidth';

import CandidateAvailabilitySidebarSection from './CandidateAvailabilitySidebarSection';
import ScheduleTaskDetails, { ScheduleTaskMenuItemProps } from './ScheduleTaskDetails';
import ScheduleTaskRequirements from './ScheduleTaskRequirements';

type Props = {
  showRequirements: boolean;
  moreMenuOptions?: ScheduleTaskMenuItemProps[];
  onMenuOptionSelected?: (option: ScheduleTaskMenuItemProps) => void;
  onHasUnsavedNoteChangesChange?: (value: boolean) => void;
  onNoteCreated?: () => void;
};

type Fragments = {
  task: ScheduleTask_TaskFragment;
};

const TOGGLE_TIMELINE_MENU_OPTION_ID = 'TOGGLE_TASK_TIMELINE';

/**
 * Renders schedule task details and its activity feed in either legacy or AI timeline layout.
 */
const ScheduleTask: FCWithFragments<Fragments, Props> = ({
  task,
  showRequirements,
  moreMenuOptions,
  onMenuOptionSelected,
  onHasUnsavedNoteChangesChange,
  onNoteCreated,
}) => {
  const { t } = useTranslation();
  const timelineViewEnabled = useFlag('TASK_TWO_COLUMN_TIMELINE_VIEW');
  const aiTaskEnabled = useIsAiTaskEnabled();
  const { resolved, timelineVisible, setTimelineVisible } = useTaskTimelineVisibility();
  const showTimeline = timelineViewEnabled && resolved && timelineVisible;

  const extensionWidth = () => {
    if (!timelineViewEnabled) {
      return 'THIN';
    }
    if (!resolved) {
      return null;
    }
    return timelineVisible ? 'THICK' : 'THIN';
  };
  useSetChromeExtensionWidth(extensionWidth());

  if (!task) {
    return null;
  }

  const { status } = task;
  const agentDelegated = aiTaskEnabled && task.delegateType === 'AGENT';
  const handleHasUnsavedNoteChangesChange = onHasUnsavedNoteChangesChange || noop;
  const handleNoteCreated = onNoteCreated || noop;

  // Original single-column layout when flag is OFF
  if (!timelineViewEnabled) {
    return (
      <Stack
        data-testid="schedule-task-legacy-layout"
        maxWidth="100%"
        direction="column"
        flexGrow={1}
        sx={{ height: '100%', minHeight: 0, overflowY: 'auto', overflowX: 'hidden', pt: 2.5 }}
      >
        <Stack spacing={2} p={2.5} pt={0}>
          <ScheduleTaskDetails
            task={task}
            moreMenuOptions={moreMenuOptions}
            onMenuOptionSelected={onMenuOptionSelected}
          />
          {!showRequirements && <CandidateAvailabilitySidebarSection task={task} />}
          {showRequirements && (
            <ScheduleTaskRequirements taskId={task.id} isCancelled={status === TaskStatus.Canceled} />
          )}
        </Stack>
        <Box
          sx={{
            backgroundColor: (theme) => theme.palette.background.contrast,
            borderTop: (theme) => `1px solid ${theme.palette.borderState.default}`,
            flexGrow: 1,
            padding: 2.5,
            mt: 0,
          }}
        >
          <ActivityFeed
            sourceId={task.id}
            sourceType={SourceType.SourceTypeTask}
            aiEnabled={agentDelegated}
            delegateStatus={task.delegateStatus}
            onHasUnsavedNoteChangesChange={handleHasUnsavedNoteChangesChange}
            onNoteCreated={handleNoteCreated}
          />
        </Box>
      </Stack>
    );
  }

  // New split-panel layout when flag is ON
  const timelineToggleLabel = showTimeline
    ? t('hiring-dashboard-tasks:tasks.hideTimeline')
    : t('hiring-dashboard-tasks:tasks.showTimeline');
  const timelineMenuOption: ScheduleTaskMenuItemProps = {
    id: TOGGLE_TIMELINE_MENU_OPTION_ID,
    icon: showTimeline ? <SidebarOnIcon fontSize="small" /> : <SidebarOffIcon fontSize="small" />,
    value: timelineToggleLabel,
  };
  const scheduleTaskMenuOptions = (() => {
    if (!resolved) {
      return moreMenuOptions;
    }

    const options = [...(moreMenuOptions ?? [])];
    const firstDividerIndex = options.findIndex((option) => option.id === DIVIDER_ID);
    options.splice(firstDividerIndex === -1 ? options.length : firstDividerIndex, 0, timelineMenuOption);
    return options;
  })();
  const handleMenuOptionSelected = (option: ScheduleTaskMenuItemProps) => {
    if (option.id !== TOGGLE_TIMELINE_MENU_OPTION_ID) {
      onMenuOptionSelected?.(option);
      return;
    }

    // Hiding unmounts the composer, so its unsaved-note guard must not stay armed.
    if (timelineVisible) {
      handleHasUnsavedNoteChangesChange(false);
    }
    setTimelineVisible(!timelineVisible);
  };

  return (
    <Stack direction="row" sx={{ height: '100%', minHeight: 0, width: '100%', overflow: 'hidden' }}>
      {/* Left panel: Task details + requirements — scrolls independently */}
      <Stack
        sx={{
          width: showTimeline ? '50%' : '100%',
          minWidth: 320,
          height: '100%',
          minHeight: 0,
          ...(showTimeline && { borderRight: 1, borderColor: 'divider' }),
        }}
      >
        <Stack sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
          <Stack spacing={2} p={2.5}>
            <ScheduleTaskDetails
              task={task}
              moreMenuOptions={scheduleTaskMenuOptions}
              onMenuOptionSelected={handleMenuOptionSelected}
            />

            {!showRequirements && <CandidateAvailabilitySidebarSection task={task} />}

            {showRequirements && (
              <ScheduleTaskRequirements taskId={task.id} isCancelled={status === TaskStatus.Canceled} />
            )}
          </Stack>
        </Stack>
      </Stack>

      {showTimeline && (
        <BottomUpTaskTimelineFeed
          taskId={task.id}
          aiEnabled={agentDelegated}
          delegateStatus={task.delegateStatus}
          onHasUnsavedNoteChangesChange={handleHasUnsavedNoteChangesChange}
          onNoteCreated={handleNoteCreated}
        />
      )}
    </Stack>
  );
};

ScheduleTask.fragments = {
  task: gql`
    ${ScheduleTaskDetails.fragments.task}
    ${CandidateAvailabilitySidebarSection.fragments.task}
    fragment ScheduleTask_task on Task {
      status
      delegateType
      delegateStatus
      application {
        id
      }
      ...ScheduleTaskDetails_task
      ...CandidateAvailabilitySidebarSection_task
    }
  `,
};

export default ScheduleTask;
